import { isBrowser, uuidv4 } from '@sortlist-frontend/utils';
import { getCookie, setCookie } from 'cookies-next';

import { getDomain } from './utils';

export const SESSION_UUID_KEY = 'session_uuid';
export const USER_UUID_KEY = 'user_uuid';
export const USER_UUID_MAX_AGE_SEVEN_DAYS_SECONDS = 7 * 24 * 60 * 60;

/**
 * Singleton manager for handling tracking UUIDs across the application
 * Ensures UUIDs are unique and only created once across all components
 */
class TrackingUuidManager {
  private static instance: TrackingUuidManager;
  private _userUuid: string | null = null;
  private _sessionUuid: string | null = null;
  private _initialized = false;

  private constructor() {
    // Initialize from cookies on instantiation
    this.initFromCookies();
  }

  /**
   * Get the singleton instance of TrackingUuidManager
   */
  public static getInstance(): TrackingUuidManager {
    if (TrackingUuidManager.instance == null) {
      TrackingUuidManager.instance = new TrackingUuidManager();
    }
    return TrackingUuidManager.instance;
  }

  private initFromCookies(): void {
    if (isBrowser() && !this._initialized) {
      const userUuidFromCookie = getCookie(USER_UUID_KEY)?.toString();
      const sessionUuidFromCookie = getCookie(SESSION_UUID_KEY)?.toString();

      if (userUuidFromCookie != null) {
        this._userUuid = userUuidFromCookie;
      }

      if (sessionUuidFromCookie != null) {
        this._sessionUuid = sessionUuidFromCookie;
      }

      this._initialized = true;
    }
  }

  /**
   * Get the current user UUID, generating and persisting one if it doesn't exist
   */
  public getUserUuid(): string {
    // Re-check cookies if not initialized yet
    if (!this._initialized) {
      this.initFromCookies();
    }

    if (this._userUuid == null) {
      this._userUuid = uuidv4();
      this.setUserUuidCookie(this._userUuid);
    }

    return this._userUuid;
  }

  /**
   * Get the current session UUID, generating and persisting one if it doesn't exist
   */
  public getSessionUuid(): string {
    // Re-check cookies if not initialized yet
    if (!this._initialized) {
      this.initFromCookies();
    }

    if (this._sessionUuid == null) {
      this._sessionUuid = uuidv4();
      this.setSessionUuidCookie(this._sessionUuid);
    }

    return this._sessionUuid;
  }

  /**
   * Reset the session UUID, generating a new one and persisting it
   * @returns The new session UUID
   */
  public resetSessionUuid(): string {
    this._sessionUuid = uuidv4();
    this.setSessionUuidCookie(this._sessionUuid);
    return this._sessionUuid;
  }

  private setUserUuidCookie(uuid: string): void {
    if (isBrowser()) {
      setCookie(USER_UUID_KEY, uuid, {
        maxAge: USER_UUID_MAX_AGE_SEVEN_DAYS_SECONDS,
        sameSite: 'lax',
        domain: getDomain(),
      });
    }
  }

  private setSessionUuidCookie(uuid: string): void {
    if (isBrowser()) {
      setCookie(SESSION_UUID_KEY, uuid, {
        sameSite: 'lax',
        domain: getDomain(),
      });
    }
  }
}

// Export the singleton instance
export const trackingUuidManager = TrackingUuidManager.getInstance();
